import { CheckCircleFilled, PlusCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { Avatar, Button, Input, Modal, notification, Popover, Select, Transfer } from "antd";
import axios from "axios";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { BsStarFill } from "react-icons/bs";
import { RiDeleteBinLine } from "react-icons/ri";
import { FeatureGroup, GeoJSON, MapContainer, TileLayer, useMap } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { AppContext } from "../AppContext";
import InfoLabel from "../components/InfoLable";
import tr from "../i18n";
import { hasAccess, ROLES } from "../utils/AccessLevels";
import { BASE_MAPS, GeoJSON_STYLE, MAP_ATTRIBUTION_URL } from "../utils/MapUtils";
import { transformTurkish } from "../utils/RegExUtility";

const { Option } = Select;

const GardenAccess = () => {
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [selectedGarden, setSelectedGarden] = useState(null);
  const [targetKeys, setTargetKeys] = useState([]);
  const [removeConfirmationVisible, setRemoveConfirmationVisible] = useState(false);

  const [, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const { gardens, loadingGardens, fetchAllGardens, role } = useContext(AppContext);
  const queryClient = useQueryClient();

  const [managedGardens, setManagedGardens] = useState([]);

  const [showLocationCreateModal, setShowLocationModal] = useState(false);

  useEffect(() => {
    console.count("GardenAccess rendered");

    setManagedGardens([...gardens]);
    setGardenActivationTargetKeys([...gardens].filter((g) => g.wateringStatus !== "active").map((g) => g.id));

    if (selectedGarden != null && gardens != null) {
      const currGarden = gardens.find((t) => t._id === selectedGarden._id);
      if (currGarden != null) {
        onGardenClick(currGarden);
      }
    } else if (gardens[0]) {
      onGardenClick(gardens[0]);
    }
  }, [gardens, selectedGarden]);

  const tags = useQuery("GardenTags", async () => {
    const res = await axios.get(process.env.REACT_APP_PLANNER_API_END_POINT + "/tag?resource=gardens");
    return res.data.tags;
  });

  useEffect(() => {
    console.count("GardenAccess useEffect fetchAllGardens");
    fetchAllGardens();
  }, [fetchAllGardens]);

  /**
   * Garden devices
   */
  const gardenDevices = useQuery(
    ["GardenDevices", selectedGarden],
    async () => {
      const res = await axios.get(process.env.REACT_APP_PLANNER_API_END_POINT + "/garden/" + selectedGarden.id + "/device");

      return res.data.devices;
    },
    { enabled: !!selectedGarden }
  );

  /**
   * Garden devices + devices available for assignment
   */
  const gardenDevicesAvailable = useQuery(
    ["GardenDevicesAvailable", selectedGarden],
    async () => {
      const res = await axios.get(process.env.REACT_APP_PLANNER_API_END_POINT + "/garden/" + selectedGarden.id + "/device/available");
      return res.data.devices;
    },
    { enabled: !!selectedGarden }
  );

  useEffect(() => {
    if (!gardenDevices.data) return;

    setTargetKeys(gardenDevices.data.map((d) => d._id));
  }, [gardenDevices.data]);

  const onTargetsChange = async (nextTargetKeys, direction, moveKeys) => {
    const gardenId = selectedGarden.id;
    if (direction === "right") {
      // adding gardens : moveKeys
      try {
        setLoading(true);

        const res = await axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/device`, {
          gardenId,
          deviceIds: moveKeys,
        });
        if (res.data.success) {
          notification.success({
            message: tr("Device assignments successful"),
            description: `${res.data.devices.length} ${tr("devices assigned")}`,
            placement: "topRight",
          });

          // if successful set target keys
          //setTargetKeys(nextTargetKeys)
          queryClient.invalidateQueries(["GardenDevices"]);
        } else {
          notification.error({
            message: tr("Something went wrong"),
            description: tr(`While assigning devices`),
            placement: "topRight",
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    if (direction === "left") {
      // removing gardens : moveKeys

      try {
        setLoading(true);
        const res = await axios.delete(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/device`, {
          data: {
            gardenId,
            deviceIds: moveKeys,
          },
        });

        if (res.data.success) {
          notification.success({
            message: tr("Device removal successful"),
            description: `${res.data.devices.length} ${tr("devices removed from garden")}`,
            placement: "topRight",
          });

          // if successful set target keys
          //setTargetKeys(nextTargetKeys)
          queryClient.invalidateQueries(["GardenDevices"]);
        } else {
          notification.error({
            message: tr("Something went wrong"),
            description: tr(`While removing devices`),
            placement: "topRight",
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const onGardenClick = (garden) => {
    setSelectedGarden(garden);
    setEditableGarden({
      name: garden.name,
      id: garden.id,
      geoJson: garden.geoJson,
    });
  };

  const filterOption = (input, item) => {
    return RegExp(`.*${transformTurkish(input)}.*`, "gi").test(transformTurkish(item.Device.name));
  };

  const onUserSearch = (e) => {
    const value = e.target.value;
    setUserSearchQuery(value);
  };

  const onTagsChange = (e) => {
    if (!e) return;

    setSelectedTags(e);
  };

  const getFilteredGardens = () => {
    let filteredGardens = managedGardens;
    if (userSearchQuery && userSearchQuery.trim().length > 1) {
      filteredGardens = filteredGardens.filter(
        (p) => RegExp(`.*${transformTurkish(userSearchQuery.trim())}.*`, "gi").test(transformTurkish(p.geoJson.properties.MAHAL_ADI)) || RegExp(`.*${userSearchQuery.trim()}.*`, "gi").test(p.id)
      );
    }

    if (selectedTags.length > 0) {
      filteredGardens = filteredGardens.filter((p) => p.tags && selectedTags.every((t) => p.tags.includes(t)));
    }

    return filteredGardens;
  };

  const [baseMap, setBaseMap] = useState(null);
  const onBaseMapChange = (m) => {
    const selected = BASE_MAPS.find((b) => b.label === m);
    setBaseMap(selected);
  };

  const [newGardenName, setNewGardenName] = useState(null);
  const [newGardenPolygon, setNewGardenPolygon] = useState(null);
  const [editableGarden, setEditableGarden] = useState(null);

  const onPolygonChange = useCallback(
    (e) => {
      console.dir(`on polygon change: ${JSON.stringify(e)}, editable Garden: ${JSON.stringify(editableGarden)}`);
      if (editableGarden) {
        setEditableGarden((prev) => ({
          ...prev,
          geoJson: {
            ...prev.geoJson,
            geometry: e,
          },
        }));
      } else {
        setNewGardenPolygon(e);
      }
    },
    [editableGarden]
  );

  const createGarden = useMutation(
    () => {
      return axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/`, {
        name: newGardenName.trim(),
        geoJson: {
          type: "Feature",
          properties: {
            MAHAL_ADI: newGardenName.trim(),
          },
          geometry: newGardenPolygon,
        },
      });
    },
    {
      onSuccess: () => {
        fetchAllGardens({
          force: true,
          background: true,
        });
        notification.success({
          message: tr("Garden created successfully"),
        });
      },
      onError: () => {
        notification.error({
          message: tr("Failed to create garden"),
        });
      },
    }
  );

  const updateGarden = useMutation(
    (garden) => {
      if (garden.geoJson.geometry && garden.geoJson.geometry.coordinates && garden.geoJson.geometry.coordinates.length === 0) {
        notification.error({
          message: tr("Map geometry is empty"),
        });
        return Promise.reject(new Error("Map geometry is empty"));
      }

      return axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/`, {
        id: garden.id,
        name: garden.name,
        geoJson: {
          type: "Feature",
          properties: {
            MAHAL_ID: garden.id,
            MAHAL_ADI: garden.name,
          },
          geometry: garden.geoJson.geometry,
        },
      });
    },
    {
      onSuccess: () => {
        setEditableGarden(null);
        fetchAllGardens({
          force: true,
          background: true,
        });
        notification.success({
          message: tr("Garden updated successfully"),
        });
      },
      onError: () => {
        notification.error({
          message: tr("Failed to update the garden"),
        });
      },
    }
  );

  const onGardenCreate = async () => {
    if (!newGardenName) {
      return notification.error({
        message: tr("Garden name is required"),
      });
    }

    if (!newGardenPolygon || newGardenPolygon.coordinates.length === 0) {
      notification.error({
        message: tr("Map area polygon is required"),
      });
      return;
    }

    await createGarden.mutateAsync();

    setNewGardenName(null);
    setNewGardenPolygon(null);
  };

  const removeGarden = useMutation(
    (id) => {
      return axios.delete(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${id}`);
    },
    {
      onSuccess: () => {
        fetchAllGardens({
          force: true,
          background: true,
        });
        notification.success({
          message: tr("Garden removed successfully"),
        });
        if (gardens[0]) {
          setSelectedGarden(gardens[0]);
        }

        setRemoveConfirmationVisible(false);
      },
      onError: () => {
        fetchAllGardens({
          force: true,
          background: true,
        });

        notification.error({
          message: tr("Failed to remove garden"),
        });

        if (gardens[0]) {
          setSelectedGarden(gardens[0]);
        }
        setRemoveConfirmationVisible(false);
      },
    }
  );

  const updateGardenOrder = useMutation(
    (gardenIds) => {
      return axios.put(process.env.REACT_APP_PLANNER_API_END_POINT + "/garden/order", {
        order: gardenIds,
      });
    },
    {
      onSuccess: () => {
        fetchAllGardens({
          force: true,
          background: true,
        });
      },
    }
  );

  const handleDragEnd = (result) => {
    if (!result.destination) return; // Return if dropped outside the list

    const updatedItems = [...managedGardens];
    const [reorderedItem] = updatedItems.splice(result.source.index, 1);
    updatedItems.splice(result.destination.index, 0, reorderedItem);

    setManagedGardens(updatedItems);
    updateGardenOrder.mutate(updatedItems.map((ud) => ud._id));
  };

  const [gardenActivationTargetKeys, setGardenActivationTargetKeys] = useState([]);
  const [multiGardenActivation, setMultiGardenActivation] = useState(false);

  const updateGardenWateringStatus = useMutation(
    ({ gardenId, wateringStatus }) => {
      return axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/`, {
        id: gardenId,
        wateringStatus,
      });
    },
    {
      onSuccess: (data, variables) => {
        notification.success({
          message: tr("Garden updated successfully"),
          description: variables.gardenId,
        });
      },
      onError: (error, variables) => {
        notification.error({
          message: tr("Failed to update the garden"),
          description: variables.gardenId,
        });
      },
    }
  );

  const updateManyWateringStatus = async (gardenIds, wateringStatus) => {
    for (const gId of gardenIds) {
      await updateGardenWateringStatus.mutateAsync({
        gardenId: gId,
        wateringStatus,
      });
    }

    fetchAllGardens({
      force: true,
      background: true,
    });
  };

  const filteredGardens = () => {
    if (selectedTags.length > 0) {
      return gardens.filter((g) => selectedTags.every((t) => g.tags && g.tags.includes(t)));
    }

    return gardens;
  };

  const gardenActivationFilterOption = (input, item) => {
    return RegExp(`.*${transformTurkish(input)}.*`, "gi").test(transformTurkish(item.geoJson.properties.MAHAL_ADI));
  };

  const onGardenActivationTargetsChange = async (nextTargetKeys, direction, moveKeys) => {
    // = selectedUser.id
    if (direction === "right") {
      // adding gardens : moveKeys
      try {
        setLoading(true);
        setGardenActivationTargetKeys(nextTargetKeys);
        updateManyWateringStatus(moveKeys, "disabled");
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    if (direction === "left") {
      // removing gardens : moveKeys

      try {
        setLoading(true);
        setGardenActivationTargetKeys(nextTargetKeys);
        updateManyWateringStatus(moveKeys, "active");
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const onGardenSearch = (dir, value) => {};

  return (
    <>
      <div className="flex maincontainer text-gray-800 rounded-xl gap-4 w-full">
        <div className="bg-white flex flex-col w-1/4 rounded-xl ">
          <div className="p-4 relative ">
            <Input style={{ borderRadius: 5 }} onChange={onUserSearch} prefix={<SearchOutlined />} placeholder="Park/Bahçe arama" allowClear />
            <div className="flex flex-col gap-1 pt-2">
              <InfoLabel>{tr("Filter gardens by tags")}</InfoLabel>
              <Select mode="tags" placeholder={tr("Filter gardens by tags")} onChange={onTagsChange}>
                {tags.data && tags.data.map((t) => <Option key={t.id}>{t.name}</Option>)}
                {tags.isLoading && <Option key={null}>{tr("Loading")}</Option>}
              </Select>
            </div>
          </div>
          {loadingGardens && (
            <>
              <div className="p-2 animate-pulse flex flex-row">
                <div className="bg-gray-200 h-5 w-5 mr-2 rounded-full"></div>
                <div className="bg-gray-200 h-5 w-full rounded-md"></div>
              </div>
              <div className="p-2 animate-pulse flex flex-row">
                <div className="bg-gray-200 h-5 w-5 mr-2 rounded-full"></div>
                <div className="bg-gray-200 h-5 w-full rounded-md"></div>
              </div>
            </>
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div className=" w-full h-full overflow-auto" {...provided.droppableProps} ref={provided.innerRef}>
                  {!loadingGardens &&
                    getFilteredGardens().map((garden, i) => (
                      <Draggable key={garden.id} draggableId={garden.id} index={i}>
                        {(provided) => (
                          <div
                            onClick={() => onGardenClick(garden)}
                            className={`flex flex-row gap-4 items-center ${selectedGarden.id === garden.id ? " bg-gray-200 " : ""} hover:bg-gray-300 p-2 ml-2 mr-2 mb-1 rounded-md `}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                          >
                            <div className=" flex gap-2 items-center">
                              <div>
                                <Avatar style={{ backgroundColor: "#999999" }} size={40}>
                                  {garden.name.substring(0, 2)}
                                </Avatar>
                              </div>
                              <div className="text-xs flex flex-col">
                                {/* {garden.id}   */}
                                {garden.tags && garden.tags.includes("gardens:favori") && <BsStarFill />}
                              </div>
                              <span className="block font-bold">{garden.name}</span>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <div className="w-full relative p-5">
            <button
              className="flex flex-row  gap-2 w-full h-9 rounded-xl text-sm font-semibold text-black border items-center text-center justify-center"
              onClick={() => {
                setShowLocationModal(true);
                setEditableGarden(null);
              }}
            >
              <PlusCircleOutlined size={30} className="items-center"></PlusCircleOutlined> {tr("Add Garden")}
            </button>
          </div>
        </div>

        <div className=" rounded-xl w-3/4 bg-white flex flex-col overflow-auto">
          {selectedGarden && (
            <div className="flex flex-row gap-4 items-center pl-4 pr-4 pt-4 w-full">
              <div className="flex">
                <Avatar size={70}>{selectedGarden.id}</Avatar>
              </div>
              <div className="flex flex-row gap-2">
                <div className="block font-bold text-lg">{selectedGarden.name}</div>
                <div className="text-green-brand flex items-center">
                  <CheckCircleFilled />
                </div>
              </div>

              {hasAccess(ROLES.TECHNICAL_ADMIN, role) && (
                <div className="flex flex-grow items-end justify-end ">
                  <Popover
                    placement="bottom"
                    visible={removeConfirmationVisible}
                    onVisibleChange={(v) => setRemoveConfirmationVisible(v)}
                    content={
                      <div className="p-3 flex flex-col items-center">
                        {tr("Are you sure you want to remove this garden?")}
                        <div>
                          <Button
                            type="link"
                            onClick={() => {
                              removeGarden.mutate(selectedGarden.id);
                            }}
                          >
                            Evet
                          </Button>{" "}
                          <Button type="link" onClick={() => setRemoveConfirmationVisible(false)}>
                            Hayır
                          </Button>
                        </div>
                      </div>
                    }
                    trigger="click"
                  >
                    <Button size="medium" type="default" onClick={() => setRemoveConfirmationVisible(true)}>
                      <div className="flex flex-row gap-3">
                        <p>{tr("Delete Garden")}</p>
                        <RiDeleteBinLine size={20}></RiDeleteBinLine>
                      </div>
                    </Button>
                  </Popover>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col gap-4 p-4 flex-grow">
            <div className=" p-4 rounded-md border flex flex-col gap-2">
              <InfoLabel>{tr("Update watering status for multiple parks")}</InfoLabel>
              <div>
                <Button onClick={() => setMultiGardenActivation(true)}>{tr("Update watering status")}</Button>
              </div>
            </div>
            <Modal
              title={tr("Update watering status for multiple parks")}
              visible={multiGardenActivation}
              okText={tr("Ok")}
              cancelText={tr("Cancel")}
              onCancel={() => setMultiGardenActivation(false)}
              onOk={() => setMultiGardenActivation(false)}
              width={"35vw"}
            >
              <div className="flex flex-col w-full items-center">
                <div className="">
                  <Transfer
                    dataSource={filteredGardens()}
                    showSearch
                    filterOption={gardenActivationFilterOption}
                    targetKeys={gardenActivationTargetKeys}
                    onChange={onGardenActivationTargetsChange}
                    onSearch={onGardenSearch}
                    render={(item) => item.name}
                    rowKey={(r) => r.id}
                    listStyle={{ width: "auto", height: 700, borderRadius: 5 }}
                    locale={{ itemUnit: "Park", itemsUnit: "Parklar", searchPlaceholder: "Park Ara", notFoundContent: "Boş" }}
                    titles={[
                      <div className="text-gray-800 font-bold text-xs">
                        Aktif
                        <span className="text-gray-500 text-xs block font-normal">Aktif parklar</span>
                      </div>,
                      <div className="text-gray-800 font-bold text-xs">
                        Pasif
                        <span className="text-gray-500 text-xs block font-normal">Pasif parklar</span>
                      </div>,
                    ]}
                  />
                </div>

                {/* <div className="flex gap-3 p-3 items-center bg-gray-100 border-t">
                            {`Apply changes to ${gardenActivationTargetKeys.length} garden${gardenActivationTargetKeys.length === 1 ? '' : 's'}`}
                            <Select defaultValue='activate' onChange={(v) => setCommonWateringStatus(v)}>
                                <Select.Option value='active'>Activate</Select.Option>
                                <Select.Option value='disable'>Disable</Select.Option>
                            </Select>
                            <Button disabled={gardenActivationTargetKeys.length === 0} onClick={() => updateManyWateringStatus(gardenActivationTargetKeys, commonWateringStatus)}>Apply</Button>
                        </div> */}
              </div>
            </Modal>

            {!selectedGarden && (
              <div className="flex p-4 border rounded-md  gap-4 ">
                <InfoLabel> Lütfen bir bahçe seçin </InfoLabel>
              </div>
            )}

            {/* Start device assignment section */}

            {/* Only roles above regional managers can add/remove devices to/from gardens */}

            {hasAccess(ROLES.TECHNICAL_ADMIN, role) && selectedGarden && (
              <div className="w-full pt-4">
                <span className="block font-bold">{tr("Add Device To Garden")}</span>
              </div>
            )}

            {hasAccess(ROLES.TECHNICAL_ADMIN, role) && (
              <div className="w-full flex flex-row">
                {gardenDevicesAvailable.isLoading && (
                  <div className="w-full flex flex-col gap-5 animate-pulse">
                    <div className="w-full flex flex-row gap-5">
                      <div className="flex flex-col w-1/3 rounded-md gap-5">
                        <div className="bg-gray-200 h-5 rounded-md"></div>
                        <div className="bg-gray-200 h-32 rounded-md"></div>
                      </div>
                      <div className="flex flex-col w-1/3 rounded-md gap-5">
                        <div className="bg-gray-200 h-5 rounded-md"></div>
                        <div className="bg-gray-200 h-32 rounded-md"></div>
                      </div>
                    </div>
                  </div>
                )}
                {selectedGarden && !gardenDevicesAvailable.isLoading && (
                  <div className="w-full">
                    <Transfer
                      dataSource={gardenDevicesAvailable.data}
                      showSearch
                      filterOption={filterOption}
                      targetKeys={targetKeys}
                      onChange={onTargetsChange}
                      render={(item) => item.Device?.name}
                      rowKey={(r) => r._id}
                      //width={'100vw'}
                      listStyle={{ width: 420, height: 200, borderRadius: 20, overflow: "hidden" }}
                      locale={{ itemUnit: "adet", itemsUnit: "adet", searchPlaceholder: "Arama", notFoundContent: "Boş" }}
                      titles={[
                        <div className="text-gray-800 font-bold text-xs">
                          Seçilebilir
                          <span className="text-gray-500 text-xs block font-normal">Boşta olan cihazlar</span>
                        </div>,
                        <div className="text-gray-800 font-bold text-xs">
                          Atanmış
                          <span className="text-gray-500 text-xs block font-normal">Bahçeye ait cihazlar</span>
                        </div>,
                      ]}
                    />
                  </div>
                )}
              </div>
            )}

            {/* End device assignment section */}

            {selectedGarden && (
              <div className="flex gap-3 pt-4">
                <div className="block font-bold">{tr("Update Garden")} </div>
              </div>
            )}

            {editableGarden && (
              <>
                <div className="flex flex-row gap-5 h-auto">
                  <div>
                    <Input
                      value={editableGarden.name}
                      onChange={(e) => {
                        setEditableGarden((p) => ({ ...p, name: e.target.value }));
                      }}
                      placeholder="MAHAL ADI"
                      style={{ borderRadius: 5 }}
                    ></Input>
                  </div>
                  <div>
                    <Button
                      onClick={() => {
                        updateGarden.mutate(editableGarden);
                      }}
                      style={{ borderRadius: 5 }}
                    >
                      {tr("Save Changes")}
                    </Button>
                  </div>
                </div>
              </>
            )}
            <div className="flex flex-wrap gap-4 h-full mb-4">
              <div className=" relative w-full h-full">
                <div className=" absolute left-1 bottom-1" style={{ zIndex: 401 }}>
                  <Select defaultValue={BASE_MAPS[1].label} onChange={onBaseMapChange}>
                    {BASE_MAPS.map((m) => (
                      <Option key={m.label}>{m.label}</Option>
                    ))}
                  </Select>
                </div>
                <MapContainer center={[0, 0]} zoom={12} scrollWheelZoom={true} zIndex={40} style={{ height: "100%" }}>
                  <MapDetails
                    garden={selectedGarden}
                    baseMap={baseMap}
                    onPolygonChange={onPolygonChange}
                    /* polygonValues={editableGarden?.geoJson?.geometry?.coordinates || null} */ polygonValues={newGardenPolygon}
                  />
                </MapContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={tr("Add Garden")}
        visible={showLocationCreateModal}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
        okText={tr("Save")}
        cancelText={tr("Cancel")}
        onCancel={() => setShowLocationModal(false)}
        onOk={() => onGardenCreate()}
      >
        <div className="flex gap-5 flex-wrap w-full">
          {
            <>
              {/*      <div>
                                <Input allowClear value={newGardenId} onChange={(e) => { let v = e.target.value.trim(); if (/^[0-9]*$/.test(v)) { setNewGardenId(v) }; }} placeholder="MAHAL ID" style={{ borderRadius: 5 }}></Input>
                            </div> */}
              <div className="flex flex-row">
                <Input
                  allowClear
                  value={newGardenName}
                  onChange={(e) => {
                    setNewGardenName(e.target.value);
                  }}
                  placeholder={tr("Park Name")}
                  style={{ borderRadius: 5 }}
                  className=" w-60"
                ></Input>
              </div>
            </>
          }

          <div className="flex flex-row relative w-full">
            <div className="absolute left-1 bottom-1" style={{ zIndex: 401 }}>
              <Select defaultValue={BASE_MAPS[1].label} onChange={onBaseMapChange}>
                {BASE_MAPS.map((m) => (
                  <Option key={m.label}>{m.label}</Option>
                ))}
              </Select>
            </div>
            <MapContainer center={[0, 0]} zoom={15} scrollWheelZoom={true} /* style={{ height: '550px' }}  */>
              <MapDetails garden={selectedGarden} baseMap={baseMap} onPolygonChange={onPolygonChange} polygonValues={newGardenPolygon} />
            </MapContainer>
          </div>
        </div>
      </Modal>
    </>
  );
};

const MapDetails = ({ garden, baseMap, onPolygonChange, polygonValues }) => {
  const map = useMap();
  const geoRef = useRef();
  const tileRef = useRef();
  const [, setPolygons] = useState([]);
  const [geometry, setGeometry] = useState([]);

  useEffect(() => {
    console.count("MapDetails rendered");

    if (!polygonValues) {
      setGeometry([]);
      map.eachLayer((l) => {
        if (l.editing) {
          map.removeLayer(l);
        }
      });
    }
  }, [map, polygonValues]);

  useEffect(() => {
    if (garden === null) map.flyTo([process.env.REACT_APP_MAP_CENTER_LAT, process.env.REACT_APP_MAP_CENTER_LNG], parseInt(process.env.REACT_APP_MAP_ZOOM), { animate: true, duration: 1.2 });
  }, [garden, map]);

  useEffect(() => {
    if (garden === null) return;
    // map.flyTo([41.084, 29.142205], 10, { animate: true, duration: 1.2 });

    if (garden?.geoJson?.geometry?.coordinates) {
      const coords = garden.geoJson.geometry.coordinates.map((c) => [...c.map(([lat, lng]) => [lng, lat])]);
      setPolygons(coords);
    }

    if (geoRef.current) {
      const bounds = geoRef.current.getBounds();
      map.flyToBounds(bounds, {
        animate: true,
        duration: 1.2,
      });
    }
  }, [garden, map]);

  useEffect(() => {
    if (!geometry) return;

    onPolygonChange({
      type: geometry.length > 1 ? "MultiPolygon" : "Polygon",
      coordinates: geometry.length > 1 ? geometry.map((g) => [g.g]) : geometry.map((g) => g.g),
    });
  }, [geometry, onPolygonChange]);

  const onEditPath = (e) => {
    e.layers.eachLayer((layer) => {
      setGeometry((p) => p.map((g) => (g.id === layer._leaflet_id ? { id: g.id, g: layer.toGeoJSON().geometry.coordinates[0] } : g)));
    });
  };

  const onCreate = (e) => {
    const type = e.layerType;
    const layer = e.layer;

    if (type === "polygon") {
      setGeometry((p) => [...p, { id: layer._leaflet_id, g: layer.toGeoJSON().geometry.coordinates[0] }]);
    }
  };

  const onDeleted = (e) => {
    e.layers.eachLayer((layer) => {
      setGeometry((p) => p.filter((g) => g.id !== layer._leaflet_id));
    });
  };

  useEffect(() => {
    if (!baseMap) return;

    if (tileRef.current) {
      tileRef.current.setUrl(baseMap.value);
    }
  }, [baseMap]);

  return (
    <>
      <FeatureGroup>
        <EditControl
          position="topright"
          onEdited={onEditPath}
          onCreated={onCreate}
          onDeleted={onDeleted}
          draw={{
            polygon: true,
            marker: false,
            polyline: false,
            rectangle: false,
            circle: false,
            circlemarker: false,
          }}
        />
        {/* {polygons.map((p, i) => <Polygon key={i} positions={p} />)} */}
      </FeatureGroup>
      {garden && (
        <GeoJSON
          style={(f) => GeoJSON_STYLE}
          key={garden.id}
          data={garden.geoJson}
          ref={geoRef}
          // onEachFeature={(feature, layer) => {
          //     layer.on('click', function (e) {
          //         e.target.editing.enable();
          //     })
          // }}
        />
      )}

      <TileLayer maxNativeZoom={17} maxZoom={100} attribution={MAP_ATTRIBUTION_URL} url={baseMap?.value || BASE_MAPS[1].value} ref={tileRef} />
    </>
  );
};

export default GardenAccess;
